import {
  ButtonERP,
  OptionsMenuStorybook,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { faFileExcel } from "@fortawesome/pro-light-svg-icons"
import { PresupuestoReadingFiltersDtoProps } from "api/DTOs/PresupuestoReadingFiltersDto"
import {
  ByFilterPresupuestoQueryParamsProps,
  getPresupuestoByFilter,
  getPresupuestoByIdSimple,
} from "api/Presupuesto.service"
import { TIPO_DOCUMENTO_COMERCIAL } from "api/Types/TipoDocumentoComercial"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import axios from "axios"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import useCrearDocumento from "common/hooks/useEnviarDocumento"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useConvertirDocumentoRedirect } from "features/Documentos/hooks/useConvertirDocumentoRedirect.hook"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import * as ROUTES from "routes/RoutePaths"
import { PRESUPUESTOS_RECIBIDOS_COLUMNS_ERP } from "../constants/PresupuestosRecibidosColumns"
import {
  handleDownloadPdfPresupuesto,
  handlerExportPresupuestosToExcel,
} from "../constants/constants"
import FilterMenu from "./FilterMenu"

export const PresupuestosRecibidos = () => {
  const { t } = useTranslation()
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [sortingCriteria, setSortingCriteria] = useState<
    ByFilterPresupuestoQueryParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: PresupuestoReadingFiltersDtoProps | null
  }>({
    show: false,
    document: null,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [selection, setSelection] = useState<
    PresupuestoReadingFiltersDtoProps[]
  >([])

  const dtRef = useRef<$TSMigrationFixMe>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const {
    activeCompany: { id: empresaActivaId },
  } = useUsuarioEmpresaActiva()
  const { crearDocumento } = useCrearDocumento()
  const { handleConvertirDocumento } = useConvertirDocumentoRedirect()

  const recibidosCancelToken = useRef(axios.CancelToken.source())

  const fetchItems = useCallback(() => {
    recibidosCancelToken.current?.cancel()
    recibidosCancelToken.current = axios.CancelToken.source()
    return (queryParams: ByFilterPresupuestoQueryParamsProps) =>
      getPresupuestoByFilter(
        {
          ...queryParams,
          LibradoId: empresaActivaId,
          SortingCriteria:
            queryParams.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
        },
        recibidosCancelToken.current.token
      )
  }, [empresaActivaId])

  const { items, isLoading, totalCount, setServiceQuery } = useFilterMenu({
    serviceCall: fetchItems,
  })

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  const onRowClick = async ({
    data,
  }: {
    data: PresupuestoReadingFiltersDtoProps
  }) => {
    if (!data.id) return
    const presupuestoById = await getPresupuestoByIdSimple(data.id)
    setModalDocumentoDetail({
      show: true,
      document: presupuestoById,
    })
  }

  return (
    <>
      <div className='tw-flex tw-flex-col tw-w-full tw-shadow-3x2 tw-bg-simple-10 tw-py-3.5 tw-px-8 tw-m-px sm:tw-flex-row sm:tw-justify-between sm:tw-flex-wrap tw-gap-2'>
        <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
          {t("crear.presupuesto.buyLabel")} (
          {isLoading ? (
            <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
          ) : (
            <span>{totalCount}</span>
          )}
          )
        </div>
        <span className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
          <FilterMenu
            serviceQuery={setServiceQuery}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />
          <ButtonERP
            customize={{
              size: "md",
              type: "secondary",
            }}
            onClick={() => crearDocumento(ROUTES.CREAR_PRESUPUESTO)}
            label={`${t("crear.presupuesto.modal.addQuotation")}`}
          />
          <OptionsMenuStorybook
            id='presupuestos-recibidos'
            items={[
              {
                title: `${t("crear.pedido.exportLabel")}`,
                hasRedirect: true,
                action: () =>
                  handlerExportPresupuestosToExcel(
                    TIPO_DOCUMENTO_COMERCIAL.COMPRA,
                    setIsLoadingExport,
                    t
                  ),
                isLoading: isLoadingExport,
                disabled: isLoadingExport,
                icon: faFileExcel,
              },
            ]}
            position='right'
          />
        </span>
      </div>

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-152px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={PRESUPUESTOS_RECIBIDOS_COLUMNS_ERP({
              t,
              handleDownloadPdfPresupuesto,
              handleConvertirDocumento,
            })}
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            lazy
            dataKey='id'
            scrollable
            selection={selection}
            selectionMode='checkbox'
            size='small'
            selectionColumnMode='multiple'
            onRowClick={onRowClick}
            onSelectionChange={({
              value,
            }: {
              originalEvent: React.SyntheticEvent
              value: PresupuestoReadingFiltersDtoProps[]
              type?: "multiple" | "all" | "checkbox" | "row" | undefined
            }) => {
              setSelection(value)
            }}
            loading={isLoading}
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/Presupuestos-de-compra.svg`}
                title='crear.presupuesto.buyLabel'
                description='shared.noDataDescription'
                actionLabel='crear.presupuesto.modal.addQuotation'
                actionCommand={() => crearDocumento(ROUTES.CREAR_PRESUPUESTO)}
              />
            }
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>

      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() =>
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }
        docType={TIPO_DOCUMENTO_ENUM.PRESUPUESTO}
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='presupuestos'
        isVenta={false}
      />
    </>
  )
}
